import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { NameListField } from "@/components/acquisitions/NameListField";
import type { RequestFormState } from "./request-form-state";

const REQUEST_FORMATS = [
  { value: "BOOK", label: "Book" },
  { value: "EBOOK", label: "E-Book" },
  { value: "JOURNAL", label: "Journal" },
  { value: "DVD", label: "DVD" },
  { value: "AUDIOBOOK", label: "Audiobook" },
  { value: "OTHER", label: "Other" },
];

type RequestTitleFieldsProps = {
  form: RequestFormState;
  onChange: (patch: Partial<RequestFormState>) => void;
  onIsbnBlur: () => void;
};

export function RequestTitleFields({ form, onChange, onIsbnBlur }: RequestTitleFieldsProps) {
  return (
    <div className="space-y-4 rounded-lg border bg-white p-6">
      <h3 className="font-semibold text-slate-900">Title information</h3>
      <div className="space-y-1.5">
        <Label htmlFor="request-title" required>
          Title
        </Label>
        <Input
          id="request-title"
          value={form.title}
          onChange={(event) => onChange({ title: event.target.value })}
          placeholder="Book or resource title"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="request-subtitle">Subtitle</Label>
        <Input
          id="request-subtitle"
          value={form.subtitle}
          onChange={(event) => onChange({ subtitle: event.target.value })}
          placeholder="Shown as Title: Subtitle"
        />
      </div>
      <NameListField
        id="request-authors"
        label="Authors"
        hint="Add each author separately. Not required."
        placeholder="Author name, then Enter"
        values={form.authors}
        onChange={(authors) => onChange({ authors })}
      />
      <NameListField
        id="request-editors"
        label="Editors"
        hint="For edited volumes. The first editor is the editor-in-chief."
        placeholder="Editor name, then Enter"
        values={form.editors}
        onChange={(editors) => onChange({ editors })}
        emphasizeFirst
      />
      <div className="space-y-1.5">
        <Label htmlFor="request-description">Description</Label>
        <Textarea
          id="request-description"
          rows={3}
          value={form.description}
          onChange={(event) => onChange({ description: event.target.value })}
          placeholder="Description of the book"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="request-isbn">ISBN</Label>
          <Input
            id="request-isbn"
            value={form.isbn}
            onChange={(event) => onChange({ isbn: event.target.value })}
            onBlur={onIsbnBlur}
            placeholder="e.g. 978-0-13-468599-1"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="request-issn">ISSN</Label>
          <Input
            id="request-issn"
            value={form.issn}
            onChange={(event) => onChange({ issn: event.target.value })}
            placeholder="For journals"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="request-publisher">Publisher</Label>
          <Input
            id="request-publisher"
            value={form.publisher}
            onChange={(event) => onChange({ publisher: event.target.value })}
            placeholder="Publisher name"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="request-place">Place of publication</Label>
          <Input
            id="request-place"
            value={form.placeOfPublication}
            onChange={(event) => onChange({ placeOfPublication: event.target.value })}
            placeholder="e.g. Lagos"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="request-year">Publication year</Label>
          <Input
            id="request-year"
            inputMode="numeric"
            maxLength={4}
            value={form.publicationYear}
            onChange={(event) =>
              onChange({ publicationYear: event.target.value.replace(/\D/g, "").slice(0, 4) })
            }
            placeholder="e.g. 2024"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="request-edition">Edition</Label>
          <Input
            id="request-edition"
            value={form.edition}
            onChange={(event) => onChange({ edition: event.target.value })}
            placeholder="e.g. 3rd"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Format</Label>
          <Select value={form.format} onValueChange={(format) => onChange({ format })}>
            <SelectTrigger>
              <SelectValue placeholder="Select format" />
            </SelectTrigger>
            <SelectContent>
              {REQUEST_FORMATS.map((format) => (
                <SelectItem key={format.value} value={format.value}>
                  {format.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
